import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../../../../config";
import UpdateAttendee from "./UpdateAttende";

const apiUrl = config.apiUrl;

const ViewAttendees = ({ onAttendeeUpdated }) => {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAttendee, setEditingAttendee] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    description: "",
    attendees: ""
  });

  const fetchAttendees = async () => {
    try {
      const response = await axios.get(`${apiUrl}/attendees`);
      setAttendees(response.data.data || response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load attendees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendees();
  }, []);

  const handleEdit = (attendee) => {
    setEditingAttendee(attendee.id);
    setFormData({
      slug: attendee.slug,
      title: attendee.title,
      description: attendee.description,
      attendees: attendee.attendees
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.put(`${apiUrl}/attendeesupdate/${editingAttendee}`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (onAttendeeUpdated) {
        onAttendeeUpdated();
      }
      await fetchAttendees();
      setEditingAttendee(null);
      alert("Attendee updated successfully!");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "An error occurred while updating the attendee.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this attendee?")) return;
    
    try {
      await axios.delete(`${apiUrl}/attendeedelete/${id}`);
      setAttendees(attendees.filter(attendee => attendee.id !== id));
      alert("Attendee deleted successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete attendee");
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading attendees...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">All Attendees</h2>
      </div>

      {attendees.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No attendees found. Create your first attendee.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attendees</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {attendees.map((attendee) => (
                <tr key={attendee.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{attendee.slug}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 font-medium">{attendee.title}</div>
                    <div className="text-sm text-gray-500 mt-1">{attendee.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{attendee.attendees}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(attendee)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(attendee.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingAttendee && (
        <div className="mt-8">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="p-6 mx-auto bg-white shadow rounded-lg min-w-[800px]">
              <UpdateAttendee
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                loading={loading}
                error={error}
                onCancel={() => setEditingAttendee(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewAttendees;